import * as Sentry from "@sentry/react";
import { Dictionary } from "lodash";
import {
  filter,
  forEach,
  groupBy,
  keyBy,
  maxBy,
  partition,
  reject,
} from "lodash/fp";

import type { PulseMetadata } from "@app/analytics/Pulse/types";
import { get } from "@app/common/api";
import apiClient from "@app/common/api/apiClient";
import type {
  ApiActionItemClip,
  ApiActionItemUpdateClip,
  ApiGptHighlight,
  ApiHighlight,
  ApiKeyQuestionAnswerClip,
  ApiPulseMetadata,
} from "@app/common/types";
import { isResponseErrorWithStatus } from "@app/common/utils";
import { parseUTCDateTimeFromString } from "@app/utils/utils";

import { parsePulseMetadata, parseSinglePulseResult } from "./utils";

const getMetadataResults = (results: ApiPulseMetadata[]) => {
  const parsedResults = parsePulseMetadata(results);
  const resultsByDate = new Map<string, PulseMetadata>();

  forEach(
    (r) => resultsByDate.set(r.dateRange.startDate.toFormat("yyyy-MM-dd"), r),
    parsedResults
  );

  return { parsedResults, resultsByDate };
};

const getWorkspaceMetadataResults = (results: ApiPulseMetadata[]) => {
  const parsedResults = parsePulseMetadata(results);

  const resultsById = new Map<string, PulseMetadata>();
  forEach((r) => resultsById.set(r.id, r), parsedResults);

  return { parsedResults, resultsById };
};

const getPulseMetadataQuery = async (): Promise<ApiPulseMetadata[]> => {
  return get("/pulse/metadata", { withCreds: true });
};

const removeRejectedAndDeletedItems = <T extends ApiHighlight>(
  clips: T[] = []
): T[] =>
  reject((c) => c.status === "user_reject" || Boolean(c.isDeleted), clips);

export const getBestAnswers = (answers: ApiKeyQuestionAnswerClip[]) => {
  const unrejectedAnswers = removeRejectedAndDeletedItems(answers);
  const answersByQuestion = groupBy("parentClipId", unrejectedAnswers);
  const bestAnswers: Dictionary<ApiKeyQuestionAnswerClip> = {};
  Object.entries(answersByQuestion).forEach(([parentClipId, answers]) => {
    if (answers.length === 1) {
      bestAnswers[parentClipId] = answers[0];
    } else {
      const [generatedByRead, userSubmitted] = partition(
        "generatedByRead",
        answers
      );
      // prioritize user-submitted answers over answers generated by Read
      bestAnswers[parentClipId] = userSubmitted[0] ?? generatedByRead[0];
    }
  });
  return bestAnswers;
};

export const getPulseClips = async (ids: string[]) => {
  const result: ApiHighlight[] = await apiClient.clips.listByIds(ids);

  const resultsByType = groupBy("clipType", result) as Record<
    ApiHighlight["clipType"],
    ApiHighlight[] | undefined
  >;

  const keyQuestionAnswersByParentId = getBestAnswers(
    (resultsByType.key_question_answer ?? []) as ApiKeyQuestionAnswerClip[]
  );

  const actionItemStatusesByParentId = keyBy(
    "parentClipId",
    removeRejectedAndDeletedItems(
      resultsByType.action_item_update as ApiActionItemUpdateClip[]
    )
  );

  const keyQuestionsById = keyBy(
    "id",
    removeRejectedAndDeletedItems(
      resultsByType.key_question as ApiGptHighlight[]
    )
  );

  const actionItemsById = keyBy(
    "id",
    removeRejectedAndDeletedItems(
      resultsByType.action_item as ApiActionItemClip[]
    )
  );

  return {
    questions: keyQuestionsById,
    answers: keyQuestionAnswersByParentId,
    statuses: actionItemStatusesByParentId,
    actionItems: actionItemsById,
  };
};

export const getPulseMetadata = async () => {
  const result = await getPulseMetadataQuery();

  const resultsByType = groupBy("pulseKind", result);

  return {
    forYou: getMetadataResults(resultsByType.for_you_page ?? []),
    tags: parsePulseMetadata(resultsByType.tag ?? []),
    workspace: getWorkspaceMetadataResults(resultsByType.workspace ?? []),
    whatYouMissed: getMetadataResults(resultsByType.what_you_missed ?? []),
    teams: parsePulseMetadata(resultsByType.team ?? []),
  };
};

export const getPulseById = async (id: string) => {
  try {
    const result = await get(`/pulse/${id}`, { withCreds: true });
    return parseSinglePulseResult(result);
  } catch (err) {
    const isPermissionError = isResponseErrorWithStatus(err, 403);
    if (isPermissionError) {
      Sentry.captureMessage(
        "Encountered permission error when requesting Pulse results"
      );
    }
    throw err;
  }
};

export const getLatestPulse = async () => {
  const metadata = await getPulseMetadataQuery();
  const forYouMetadata = filter({ pulseKind: "for_you_page" }, metadata);
  const latestMetadata = maxBy(
    (m) => parseUTCDateTimeFromString(m.dateRange.end).toMillis(),
    forYouMetadata
  );

  if (!latestMetadata) return null;

  return getPulseById(latestMetadata.id);
};
